import { AloftArea, AloftAreaJSON, AloftPriceJSON } from '@aloft/shared-objects';
import {
  AloftPrice,
  Improvements,
  ManualAdjustmentKey,
  PropertyData,
  SixTierQuality,
  schAloftArea,
  schAloftPrice,
} from '@aloft/toolkit-shared';
import { Opts, ProductColor } from '@aloft/toolkit-sui';

import { isUndefined } from 'lodash';

import { CompAdjustments } from '../../../redux/features/selectors/CompAdjustments.ts';
import { getFeatureQualityLabel } from '../../../redux/utils/getFeatureQualityLabel.ts';
import { PropertyGridField } from '../types.ts';

export const getCompAdjustmentValue = (
  manualAdjustmentKey: ManualAdjustmentKey,
  compAdjustments?: CompAdjustments,
): AloftPrice => {
  if (!compAdjustments || !manualAdjustmentKey) return AloftPrice.zero;

  switch (manualAdjustmentKey) {
    case 'gla':
      return compAdjustments.mainDwelling.gla.adjustment ?? AloftPrice.zero;
    case 'poolSpa':
      return compAdjustments.pool.adjustment ?? AloftPrice.zero;
    case 'site':
      return compAdjustments.site.size.adjustment ?? AloftPrice.zero;
    case 'units':
      return compAdjustments.mainDwelling.units?.adjustment ?? AloftPrice.zero;
    default:
      return compAdjustments[manualAdjustmentKey].adjustment ?? AloftPrice.zero;
  }
};

export const getAdjustmentColor = (adjustment?: AloftPrice): ProductColor => {
  if (!adjustment || adjustment.equals(AloftPrice.zero)) return 'textPrimary';
  if (adjustment.lt(AloftPrice.zero)) return 'alertError';
  return 'alertSuccess';
};

export const getAloftAreaJSONPropertyGridField = (
  fieldName: PropertyGridField<AloftAreaJSON>['fieldName'],
  label: PropertyGridField<AloftAreaJSON>['label'] = 'Area',
  manualAdjustmentKey?: PropertyGridField<AloftAreaJSON>['manualAdjustmentKey'],
): PropertyGridField<AloftAreaJSON> => ({
  fieldName,
  label,
  manualAdjustmentKey,
  toDisplayValue: (value) => (value ? schAloftArea.parse(value)?.getSiteAreaString() : undefined),
  toEditValue: (value) =>
    value ? schAloftArea.parse(value)?.toSqFt().unitAmount.toString() : undefined,
  toValue: (value) => {
    if (isUndefined(value)) return undefined;

    const area = schAloftArea.safeParse(value);
    if (area.success) return area.data.toJSON();

    // Handle partial input gracefully (e.g., just "-" or empty string)
    const trimmedValue = value.trim();
    if (trimmedValue === '' || trimmedValue === '-') return undefined;

    try {
      return AloftArea.fromString(value, 'sqft').toJSON();
    } catch {
      // If parsing fails, return undefined to allow partial input
      return undefined;
    }
  },
});

export const getAloftPriceJSONPropertyGridField = (
  fieldName: PropertyGridField<AloftPriceJSON>['fieldName'],
  label: PropertyGridField<AloftPriceJSON>['label'] = 'Cost',
  manualAdjustmentKey?: PropertyGridField<AloftPriceJSON>['manualAdjustmentKey'],
): PropertyGridField<AloftPriceJSON> => ({
  fieldName,
  label,
  manualAdjustmentKey,
  toDisplayValue: (value) => (value ? schAloftPrice.parse(value)?.toString() : undefined),
  toEditValue: (value) => {
    if (!value) return undefined;
    const price = schAloftPrice.parse(value);
    if (!price) return undefined;
    return price.toDollarLike().toString();
  },
  toValue: (value) => {
    if (isUndefined(value)) return undefined;

    const price = schAloftPrice.safeParse(value);
    if (price.success) return price.data.toJSON();

    const trimmedValue = value.trim();
    if (trimmedValue === '' || trimmedValue === '$') return undefined;

    try {
      return AloftPrice.fromDollarLike(value).toJSON();
    } catch {
      return undefined;
    }
  },
});

export const getNumberPropertyGridField = (
  fieldName: PropertyGridField<number>['fieldName'],
  label: PropertyGridField<number>['label'],
  manualAdjustmentKey?: PropertyGridField<number>['manualAdjustmentKey'],
  options?: PropertyGridField<number>['options'],
  autofillSubjectValue?: boolean,
): PropertyGridField<number> => ({
  fieldName,
  label,
  manualAdjustmentKey,
  options,
  autofillSubjectValue,
  toValue: (value) => (isUndefined(value) ? undefined : parseFloat(value)),
});

export const getCraftsmanQualityPropertyGridField = (
  fieldName: PropertyGridField<number>['fieldName'],
  label: PropertyGridField<number>['label'] = 'Craftsman Quality',
  manualAdjustmentKey?: PropertyGridField<number>['manualAdjustmentKey'],
): PropertyGridField<number> => {
  const options: Opts<SixTierQuality> = [];

  if (['pool', 'spa'].some((feature) => fieldName.includes(feature))) {
    options.push({ label: 'Luxury', value: 1 });
    options.push({ label: 'Better', value: 2 });
    options.push({ label: 'Standard', value: 3 });
  } else if (['barn', 'shed', 'workshop'].some((feature) => fieldName.includes(feature))) {
    options.push({ label: 'Good', value: 1 });
    options.push({ label: 'Average', value: 2 });
    options.push({ label: 'Low', value: 3 });
  } else {
    options.push({ label: 'Luxury', value: 1 });
    options.push({ label: 'Semi-Luxury', value: 2 });
    options.push({ label: 'Best', value: 3 });
    options.push({ label: 'Good', value: 4 });
    options.push({ label: 'Average', value: 5 });
    options.push({ label: 'Minimum', value: 6 });
  }

  return getNumberPropertyGridField(fieldName, label, manualAdjustmentKey, options);
};

export const getAreaAndQualityFeatureAdjustmentGridField = (
  type: 'barn' | 'shed' | 'workshop',
): PropertyGridField<PropertyData['editableData']['improvements']> => ({
  fieldName: 'editableData.improvements',
  label: type.charAt(0).toUpperCase() + type.slice(1),
  manualAdjustmentKey: type,
  toDisplayValue: (improvements?: Improvements) => {
    if (!improvements?.[type]) return undefined;
    const { area, quality } = improvements[type];

    if (!area || !quality) return undefined;

    const areaString = AloftArea.schema.parse(area).getSiteAreaString('');
    const qualityString = getFeatureQualityLabel(type, quality, true);

    return `${areaString}/${qualityString}`;
  },
});

export const suffixField = <T extends PropertyGridField<string> | PropertyGridField<number>>(
  field: T,
  suffix: string,
  suffixPlural?: string,
): T => ({
  ...field,
  toDisplayValue: (value: string | number | undefined) => {
    if (isUndefined(value)) return undefined;

    let suffixToUse = suffix;

    if (typeof value === 'number' && suffixPlural && Math.abs(value) !== 1) {
      suffixToUse = suffixPlural;
    }

    return [value, suffixToUse].join(' ');
  },
  toEditValue: (value: string | undefined) => {
    if (isUndefined(value)) return undefined;
    return value;
  },
  toValue: (value) => {
    if (isUndefined(value)) return undefined;

    const parsed = value
      .toString()
      .replace(
        suffixPlural && value.toString().endsWith(suffixPlural)
          ? new RegExp(`${suffixPlural}$`)
          : new RegExp(`${suffix}$`),
        '',
      )
      .trim();

    const numberValue = parseFloat(parsed);

    return Number.isNaN(numberValue) ? parsed : numberValue;
  },
});
